// src/components/FlipbookViewer.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import HTMLFlipBook from 'react-pageflip';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/entry.webpack'; 
import { db } from '../../../firebase'; // Adjusted path
import './FlipbookViewer.css'; // Adjusted path

// Set workerSrc for react-pdf (essential for it to work)
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// --- Page Cover Component ---
const PageCover = React.forwardRef((props, ref) => {
    return (
        <div className="page page-cover" ref={ref} data-density="hard">
            <div className="page-content">
                <h2>{props.children}</h2>
            </div>
        </div>
    );
});

// --- PDF Page Content Component ---
const PageContent = React.forwardRef((props, ref) => {
    return (
        <div className="page" ref={ref}>
            <div className="page-content">
                {/* The Page component from react-pdf renders the actual PDF page */}
                <Page
                    key={`page_${props.pageNumber}`} // Unique key per page
                    pageNumber={props.pageNumber}
                    width={props.pageWidth}
                    renderTextLayer={false}       // Disable text layer if not needed for performance
                    renderAnnotationLayer={false} // Disable annotation layer if not needed
                    loading={<div style={{ height: props.pageHeight, width: props.pageWidth, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading page...</div>} // Basic page loading indicator
                />
                <div className="page-footer">{props.pageNumber}</div>
            </div>
        </div>
    );
});

// --- Main Flipbook Viewer Component ---
function FlipbookViewer() {
    const { flipbookId } = useParams();
    const [flipbookData, setFlipbookData] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [isFetchingData, setIsFetchingData] = useState(true); // Firestore fetch status
    const [isPdfLoading, setIsPdfLoading] = useState(false);    // PDF document loading status
    const [pdfLoadProgress, setPdfLoadProgress] = useState(0); // PDF download progress
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(0); // 0-based index from react-pageflip
    const [bookWidth, setBookWidth] = useState(500);   // Initial width, will be updated
    const [bookHeight, setBookHeight] = useState(700); // Initial height, will be updated
    const flipBookRef = useRef();
    const containerRef = useRef();

    // --- Responsive Book Size Calculation ---
    const updateBookSize = useCallback(() => {
        if (containerRef.current) {
            const parentWidth = containerRef.current.offsetWidth;
            // Aim for a two-page spread width that fits well
            const targetWidth = Math.min(parentWidth * 0.95, 1100); // Use 95% width, max 1100px

            // Estimate height based on common PDF aspect ratio (A4 ~ 1:1.414)
            // Width of two pages = targetWidth
            // Width of one page = targetWidth / 2
            // Height = (Width of one page) * 1.414 = (targetWidth / 2) * 1.414
            const targetHeight = Math.max((targetWidth / 2) * 1.414, 450); // Ensure minimum height

            // Ensure even numbers for potentially smoother rendering in react-pageflip
            setBookWidth(Math.floor(targetWidth / 2) * 2);
            setBookHeight(Math.floor(targetHeight / 2) * 2);
        }
    }, []); // No dependencies needed inside useCallback if it only reads refs/constants

    // --- Effect for Initial Size and Resize ---
    useEffect(() => {
        window.addEventListener('resize', updateBookSize);
        // Calculate size shortly after mount allows layout to potentially settle
        const timer = setTimeout(updateBookSize, 100);

        return () => {
            window.removeEventListener('resize', updateBookSize);
            clearTimeout(timer);
        };
    }, [updateBookSize]); // Rerun if updateBookSize function reference changes

    // --- Effect for Fetching Flipbook Data from Firestore ---
    useEffect(() => {
        // Reset states when flipbookId changes
        setIsFetchingData(true);
        setError('');
        setFlipbookData(null);
        setPdfUrl(null);
        setNumPages(null);
        setIsPdfLoading(false);
        setPdfLoadProgress(0);
        setCurrentPage(0); // Reset page number

        if (!flipbookId) {
            setError('Invalid flipbook ID provided.');
            setIsFetchingData(false);
            return;
        }

        const fetchFlipbook = async () => {
            try {
                const querySnapshot = await db.collection('flipbooks')
                                           .where('flipbookId', '==', flipbookId)
                                           .limit(1)
                                           .get();

                if (querySnapshot.empty) {
                    setError(`Flipbook with ID '${flipbookId}' not found.`);
                } else {
                    const doc = querySnapshot.docs[0];
                    const data = doc.data();
                    if (data && data.pdfUrl && data.name) {
                         setFlipbookData(data);
                         setPdfUrl(data.pdfUrl); // This will trigger the <Document> component to load
                         setIsPdfLoading(true); // Set flag indicating PDF load should begin
                         setError(''); // Clear any previous error
                    } else {
                        setError('Flipbook data is incomplete (missing URL or name).');
                    }
                }
            } catch (err) {
                console.error("Error fetching flipbook data:", err);
                setError('Failed to load flipbook data. Check console for details.');
            } finally {
                setIsFetchingData(false); // Firestore fetch attempt is complete
            }
        };

        fetchFlipbook();

    }, [flipbookId]); // Re-run only if flipbookId changes

    // --- react-pdf Document Callbacks ---
    const onDocumentLoadSuccess = ({ numPages: nextNumPages }) => {
        setNumPages(nextNumPages);
        setIsPdfLoading(false); // PDF metadata loaded successfully
        setPdfLoadProgress(100); // Mark progress as complete
        setError(''); // Clear potential transient errors like CORS if resolved
        updateBookSize(); // Recalculate size now that we know the PDF is loaded
    };

    const onDocumentLoadError = (err) => {
        console.error("Error loading PDF document:", err);
        // Provide a more specific hint about CORS
        let errorMessage = `Failed to load PDF: ${err.message}.`;
        if (err.message.toLowerCase().includes('failed to fetch')) {
            errorMessage += ' This often indicates a CORS configuration issue on Firebase Storage. Ensure your website domain is allowed.';
        }
        setError(errorMessage);
        setIsPdfLoading(false); // Stop PDF loading attempt
        setPdfLoadProgress(0); // Reset progress
        setNumPages(null);     // Ensure flipbook doesn't render
    };

    const onDocumentLoadProgress = ({ loaded, total }) => {
        if (total > 0) { // Avoid division by zero if total is somehow not available initially
           const progress = Math.round((loaded / total) * 100);
           setPdfLoadProgress(progress);
        }
    };

    // --- react-pageflip Interaction ---
    const onFlip = (e) => {
        // e.data contains the current page number (0-based index)
        setCurrentPage(e.data);
    };

    // --- Share Functionality ---
    const handleShareWhatsApp = () => {
        const shareLink = window.location.href; // Get current page URL
        const message = `Check out this flipbook: ${flipbookData?.name || 'Untitled Flipbook'}\n${shareLink}`;
        const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    };

    // --- Render Logic ---

    // Calculate single page width (needed for react-pdf Page component)
    // This calculation happens *after* bookWidth state has been updated
    const singlePageWidth = bookWidth / 2;

    // 1. Handle Initial Data Fetching State
    if (isFetchingData) {
        return <div className="viewer-status">Loading Flipbook Data...</div>;
    }

    // 2. Handle Firestore Errors (if PDF isn't already loading/loaded)
    // Don't show Firestore error if we are actively trying to load PDF or if PDF loaded successfully
    if (error && !isPdfLoading && !numPages) {
        return <div className="viewer-status error">{error}</div>;
    }

    // 3. Handle case where URL wasn't found (should be caught by error state, but as safeguard)
    if (!pdfUrl && !isFetchingData && !error) {
        return <div className="viewer-status error">Flipbook PDF URL not available.</div>;
    }

    // Calculate total pages including covers for display/navigation logic
    // Total views = Front Cover (1) + PDF Pages (numPages) + Back Cover (1) = numPages + 2
    // react-pageflip indices go from 0 to numPages + 1
    const totalViews = numPages ? numPages + 1 : 0;


    return (
        <div className="flipbook-viewer-container" ref={containerRef}>
            {flipbookData && <h2 className="flipbook-title">{flipbookData.name}</h2>}

            {/* Status display during PDF loading or if there's an error during/after loading */}
            {(isPdfLoading || (error && pdfUrl)) && (
                 <div className="viewer-status loading-pdf">
                    <span>
                        {isPdfLoading ? `Loading PDF... ${pdfLoadProgress}%` : `Error loading PDF`}
                    </span>
                     {isPdfLoading && (
                         <div className="progress-bar-container">
                            <div
                                className="progress-bar"
                                style={{ width: `${pdfLoadProgress}%` }}
                            ></div>
                        </div>
                     )}
                    {/* Display specific PDF error message if it occurs */}
                    {error && pdfUrl && <p className="error-message-inline">{error}</p>}
                 </div>
            )}

            {/* Document and Flipbook Wrapper */}
            {/* Render Document as soon as pdfUrl is available to start fetching */}
            {/* Hide the wrapper visually until PDF is loaded and ready */}
            {pdfUrl && (
                <div
                    className="flipbook-wrapper"
                    style={{
                        visibility: !isPdfLoading && numPages ? 'visible' : 'hidden',
                        height: bookHeight // Set height to prevent layout shifts
                     }}
                >
                    <Document
                        file={pdfUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        onLoadProgress={onDocumentLoadProgress}
                        loading="" // We handle loading UI outside
                        error=""   // We handle error UI outside
                        options={{
                            cMapUrl: `//cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
                            cMapPacked: true,
                          }}
                    >
                        {/* Render HTMLFlipBook only when numPages is known and PDF is not loading */}
                        {!isPdfLoading && numPages && (
                            <HTMLFlipBook
                                width={singlePageWidth}    // Single page width
                                height={bookHeight}        // Full book height
                                size="stretch"             // Adjusts pages to fit container
                                minWidth={singlePageWidth * 0.9} // Allow slight shrinking
                                maxWidth={singlePageWidth}
                                minHeight={bookHeight * 0.9}
                                maxHeight={bookHeight}
                                drawShadow={true}
                                maxShadowOpacity={0.5}
                                showCover={true}          // Use the first child as a cover
                                mobileScrollSupport={true}
                                onFlip={onFlip}           // Callback for page changes
                                className="flipbook-object"
                                ref={flipBookRef}
                                key={pdfUrl} // Force re-mount if URL changes drastically
                            >
                                {/* --- Front Cover --- */}
                                <PageCover>{flipbookData?.name || 'Cover'}</PageCover>

                                {/* --- PDF Pages --- */}
                                {Array.from({ length: numPages }, (_, index) => (
                                    <PageContent
                                        key={`page_${index + 1}`}
                                        pageNumber={index + 1} // react-pdf page numbers are 1-based
                                        pageWidth={singlePageWidth}
                                        pageHeight={bookHeight} // Pass height for loader styling
                                     />
                                ))}

                                {/* --- Back Cover --- */}
                                <PageCover>End</PageCover>

                            </HTMLFlipBook>
                        )}
                    </Document>
                </div>
            )}


            {/* Navigation and Share Controls - Show only when flipbook is ready */}
            {/* Check numPages specifically to ensure PDF loaded */}
            {numPages && !isPdfLoading && !error && (
                <>
                    <div className="navigation-controls">
                        <button
                            onClick={() => flipBookRef.current?.pageFlip().flipPrev()}
                            disabled={currentPage === 0} // Disabled on the front cover (index 0)
                        >
                            Previous
                        </button>
                        <span>
                            Page {currentPage} of {totalViews}
                            {/* Provide more context about which PDF page is shown */}
                            {currentPage > 0 && currentPage <= numPages && ` (PDF Page ${currentPage})`}
                            {currentPage === 0 && ' (Cover)'}
                            {currentPage === totalViews && ' (Back Cover)'}
                        </span>
                        <button
                            onClick={() => flipBookRef.current?.pageFlip().flipNext()}
                            disabled={currentPage >= totalViews} // Disabled on the back cover (index numPages + 1)
                        >
                             Next 
                        </button>
                    </div>
                    <div className="share-controls">
                        <button onClick={handleShareWhatsApp} className="share-button whatsapp">
                            Share on WhatsApp
                        </button>
                        {/* Add more share options if needed */}
                    </div>
                </>
            )}
        </div>
    );
}

export default FlipbookViewer;